/**
 * Consent Management Service
 * Frontend API client for consent management
 */

import { http } from "@/lib/http";
import { getAuthToken } from "../utils/authToken";

// Keep host part only; service endpoints already include /api/... paths.
const API_BASE_URL = (import.meta.env.VITE_API_URL || "").replace(
  /\/api\/?$/,
  "",
);

export enum ConsentType {
  // Data Processing
  DATA_PROCESSING = "DATA_PROCESSING",
  DATA_STORAGE = "DATA_STORAGE",

  // Sharing
  SHARE_WITH_HEALTHCARE_PROVIDERS = "SHARE_WITH_HEALTHCARE_PROVIDERS",
  SHARE_WITH_TOUR_OPERATORS = "SHARE_WITH_TOUR_OPERATORS",
  SHARE_WITH_INSURERS = "SHARE_WITH_INSURERS",
  SHARE_WITH_RESEARCH = "SHARE_WITH_RESEARCH",

  // Communication
  EMAIL_COMMUNICATIONS = "EMAIL_COMMUNICATIONS",
  SMS_NOTIFICATIONS = "SMS_NOTIFICATIONS",
  PUSH_NOTIFICATIONS = "PUSH_NOTIFICATIONS",

  // Analytics
  USAGE_ANALYTICS = "USAGE_ANALYTICS",
  PERFORMANCE_MONITORING = "PERFORMANCE_MONITORING",

  // Marketing
  MARKETING_COMMUNICATIONS = "MARKETING_COMMUNICATIONS",
  THIRD_PARTY_MARKETING = "THIRD_PARTY_MARKETING",

  // Unit Conversion Specific
  INTERNATIONAL_UNIT_CONVERSION = "INTERNATIONAL_UNIT_CONVERSION",
  FHIR_DATA_SHARING = "FHIR_DATA_SHARING",
}

export enum ConsentStatus {
  GRANTED = "GRANTED",
  DENIED = "DENIED",
  WITHDRAWN = "WITHDRAWN",
  EXPIRED = "EXPIRED",
}

export interface ConsentRecord {
  _id?: string;
  userId: string;
  consentType: ConsentType;
  status: ConsentStatus;
  grantedAt?: string;
  withdrawnAt?: string;
  expiresAt?: string;
  purpose: string;
  legalBasis: string;
  version: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

class ConsentService {
  private getAuthHeaders() {
    const token = getAuthToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Grant a consent
   */
  async grantConsent(
    consentType: ConsentType,
    purpose: string,
    legalBasis: string = "Consent",
    version: string = "1.0",
    expiresInDays?: number,
  ): Promise<ConsentRecord> {
    const response = await http.post(
      `${API_BASE_URL}/api/consent/grant`,
      {
        consentType,
        purpose,
        legalBasis,
        version,
        expiresInDays,
      },
      this.getAuthHeaders(),
    );

    return response.data.data;
  }

  /**
   * Withdraw a consent
   */
  async withdrawConsent(
    consentType: ConsentType,
    reason?: string,
  ): Promise<void> {
    await http.post(
      `${API_BASE_URL}/api/consent/withdraw`,
      {
        consentType,
        reason,
      },
      this.getAuthHeaders(),
    );
  }

  /**
   * Grant multiple consents at once
   */
  async batchGrantConsents(
    consents: Array<{ consentType: ConsentType; purpose: string }>,
    legalBasis: string = "Consent",
    version: string = "1.0",
  ): Promise<ConsentRecord[]> {
    const response = await http.post(
      `${API_BASE_URL}/api/consent/batch-grant`,
      {
        consents,
        legalBasis,
        version,
      },
      this.getAuthHeaders(),
    );

    return response.data.data.consents;
  }

  /**
   * Get all consents for current user
   */
  async getMyConsents(): Promise<ConsentRecord[]> {
    const response = await http.get(
      `${API_BASE_URL}/api/consent/my-consents`,
      this.getAuthHeaders(),
    );

    return response.data.data.consents;
  }

  /**
   * Get active consents only
   */
  async getActiveConsents(): Promise<ConsentRecord[]> {
    const response = await http.get(
      `${API_BASE_URL}/api/consent/active`,
      this.getAuthHeaders(),
    );

    return response.data.data.consents;
  }

  /**
   * Get consent summary for transparency
   */
  async getConsentSummary(): Promise<unknown> {
    const response = await http.get(
      `${API_BASE_URL}/api/consent/summary`,
      this.getAuthHeaders(),
    );

    return response.data.data;
  }

  /**
   * Check if user has given a specific consent
   */
  async hasConsent(consentType: ConsentType): Promise<boolean> {
    const response = await http.get(
      `${API_BASE_URL}/api/consent/check/${consentType}`,
      this.getAuthHeaders(),
    );

    return response.data.data.hasConsent;
  }
}

export const consentService = new ConsentService();
